import { useEffect, useState } from "react";
import { Alert, Box, IconButton, Stack } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Trans } from "react-i18next";
import { useLocales } from "../../../locales";
import { useAuthContext } from "../../../context-state/AuthContext";
import Iconify from "../../../components/minimal/iconify";
import * as API from "../../../api";

// The BE allows one verification email per 60s per user.
const RESEND_COOLDOWN_MS = 60 * 1000;

// Dismissing hides the banner until the next login: the key carries the session token's tail, so a new
// login (new token) brings it back. sessionStorage can throw (private mode, blocked storage) — then the
// dismiss just doesn't persist across reloads.
const dismissKey = (user) => `verify-email-banner-dismissed:${user?._id}:${String(user?.token || '').slice(-16)}`;
const readDismissed = (user) => {
    try {
        return sessionStorage.getItem(dismissKey(user)) === '1';
    } catch {
        return false;
    }
};
const writeDismissed = (user) => {
    try {
        sessionStorage.setItem(dismissKey(user), '1');
    } catch {
        // ignore — see dismissKey
    }
};

/** Reminder for a dashboard user who hasn't verified their email (#1447). Nothing is blocked. */
export const VerifyEmailBanner = () => {

    const { translate } = useLocales();
    const { user, updateUser } = useAuthContext();
    const [isDismissed, setIsDismissed] = useState(() => readDismissed(user));
    const [isSending, setIsSending] = useState(false);
    const [result, setResult] = useState(null); // { severity, message }
    const [cooldownUntil, setCooldownUntil] = useState(0);

    useEffect(() => {
        if (!cooldownUntil) return undefined;
        const timer = setTimeout(() => setCooldownUntil(0), cooldownUntil - Date.now());
        return () => clearTimeout(timer);
    }, [cooldownUntil]);

    // Strict false: a user object without the field (older BE response) never gets the banner.
    if (!user || user.isEmailVerified !== false || isDismissed) {
        return null;
    }

    const dismiss = () => {
        writeDismissed(user);
        setIsDismissed(true);
    };

    const resend = async () => {
        try {
            setIsSending(true);
            setResult(null);
            await API.resendVerificationEmail();
            setResult({ severity: 'success', message: translate('auth.verify_email.banner_sent', { email: user.email }) });
            setCooldownUntil(Date.now() + RESEND_COOLDOWN_MS);
        } catch (e) {
            const data = e?.details?.data;
            // Verified on another device since this session loaded the user.
            if (data?.error === 'email_already_verified') {
                updateUser({ ...user, isEmailVerified: true });
                return;
            }
            // 429 (resend too soon) carries a translated userFacingMessage; never show the raw English `message`.
            setResult({ severity: 'error', message: data?.userFacingMessage || translate('general_error') });
        } finally {
            setIsSending(false);
        }
    };

    return (
        <Box sx={{ mb: 2 }} data-testid="verify-email-banner">
            <Alert
                severity={result?.severity || 'warning'}
                // Narrow screens: the actions wrap to their own row instead of squeezing the button text.
                sx={{
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    '& .MuiAlert-message': { flex: '1 1 220px' },
                    '& .MuiAlert-action': { ml: 'auto', pt: 0 },
                }}
                action={
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <LoadingButton
                            size="small"
                            color="inherit"
                            variant="outlined"
                            loading={isSending}
                            disabled={!!cooldownUntil}
                            onClick={resend}
                            sx={{ whiteSpace: 'nowrap' }}
                        >
                            {translate('auth.verify_email.banner_send')}
                        </LoadingButton>
                        <IconButton size="small" color="inherit" onClick={dismiss} aria-label={translate('auth.verify_email.banner_dismiss')}>
                            <Iconify icon="eva:close-fill" />
                        </IconButton>
                    </Stack>
                }
            >
                {result?.message || (
                    <Trans
                        i18nKey="auth.verify_email.banner_not_verified"
                        values={{ email: user.email }}
                        components={{ strong: <strong dir="ltr" /> }}
                    />
                )}
            </Alert>
        </Box>
    );
};
